import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import API from '../../utils/api';
import toast from 'react-hot-toast';

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ name: '', username: '', password: '' });
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetch = () => API.get('/employees').then(r => setEmployees(r.data)).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  useEffect(() => { fetch(); }, []);

  const save = async () => {
    if (!form.name || !form.username) return toast.error('Name and username are required');
    if (!editing && !form.password) return toast.error('Password is required');
    
    setSaving(true);
    try {
      if (editing) {
        await API.put(`/employees/${editing.id}`, { name: form.name, password: form.password || undefined });
        toast.success('Employee details updated');
      } else {
        await API.post('/employees', form);
        toast.success('Employee created successfully');
      }
      fetch();
      setModal(false);
      setEditing(null);
      setForm({ name: '', username: '', password: '' });
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    setDeletingId(id);
    try {
      await API.delete(`/employees/${id}`);
      toast.success('Employee deleted');
      fetch();
    } catch {
      toast.error('Failed to delete employee');
    } finally {
      setDeletingId(null);
    }
  };

  const handleEdit = (emp) => {
    setEditing(emp);
    setForm({ name: emp.name, username: emp.username, password: '' });
    setModal(true);
  };

  const handleCloseModal = () => {
    setModal(false);
    setEditing(null);
    setForm({ name: '', username: '', password: '' });
  };

  return (
    <Layout title="Employee Management" actions={<button className="btn btn-gold" onClick={() => setModal(true)}>+ Create Employee</button>}>
      <div className="card">
        <div className="card-header"><span className="card-title">All Employees ({employees.length})</span></div>
        <div className="table-wrap">
          {loading ? <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-400)' }}>Loading...</div> :
            employees.length === 0 ? <div className="empty-state"><div className="empty-icon">👤</div><div className="empty-title">No employees yet</div><div className="empty-text">Create employee accounts to allow billing access</div></div> : (
              <table>
                <thead><tr><th>#</th><th>Employee</th><th>Username</th><th>Bills Created</th><th>Joined</th><th>Actions</th></tr></thead>
                <tbody>
                  {employees.map((e, i) => (
                    <tr key={e.id}>
                      <td style={{ color: 'var(--gray-500)' }}>{i + 1}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--navy)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.9rem', flexShrink: 0 }}>{e.name[0]}</div>
                          <strong style={{ color: 'var(--navy)' }}>{e.name}</strong>
                        </div>
                      </td>
                      <td><code style={{ background: 'var(--gray-100)', padding: '3px 8px', borderRadius: 4, fontSize: '0.82rem' }}>{e.username}</code></td>
                      <td><span className="badge badge-navy">{e.billCount} bills</span></td>
                      <td>{new Date(e.createdAt).toLocaleDateString('en-IN')}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button className="btn btn-outline btn-sm" onClick={() => handleEdit(e)} disabled={deletingId !== null || saving}>✏️ Edit</button>
                          <button className="btn btn-danger btn-sm" onClick={() => setConfirmDelete(e)} disabled={deletingId !== null || saving}>
                            {deletingId === e.id ? '⏳ Removing...' : '🗑️ Remove'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && handleCloseModal()}>
          <div className="modal">
            <div className="modal-header"><span className="modal-title">{editing ? 'Edit Employee Details' : 'Create Employee Account'}</span><button className="modal-close" onClick={handleCloseModal}>×</button></div>
            <div className="modal-body">
              <div style={{ background: 'var(--blue-light)', border: '1px solid #bee3f8', borderRadius: 'var(--radius-sm)', padding: '10px 14px', marginBottom: 18, fontSize: '0.82rem', color: 'var(--blue)' }}>
                {editing ? '🔐 Leave the password field blank to keep the current password' : '🔐 Employee will use these credentials to log in and create bills'}
              </div>
              <div className="form-group"><label className="form-label">Full Name *</label><input className="form-control" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Employee full name" /></div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Username *</label><input className="form-control" value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} placeholder="Login username" disabled={!!editing} /></div>
                <div className="form-group"><label className="form-label">{editing ? 'Change Password' : 'Password *'}</label><input className="form-control" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder={editing ? "Enter new password (optional)" : "Set password"} /></div>
              </div>
            </div>
            <div className="modal-footer"><button className="btn btn-outline" onClick={handleCloseModal} disabled={saving}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? '⏳ Saving...' : (editing ? 'Save Changes' : 'Create Account')}</button></div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="confirm-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="confirm-dialog" onClick={e => e.stopPropagation()}>
            <div className="confirm-icon">⚠️</div>
            <div className="confirm-title">Delete Employee?</div>
            <div className="confirm-desc">
              Are you sure you want to delete employee <strong>{confirmDelete.name}</strong>? This will preserve their bills but detach their account.
            </div>
            <div className="confirm-buttons">
              <button className="btn btn-outline" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={() => {
                const id = confirmDelete.id;
                setConfirmDelete(null);
                remove(id);
              }}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
